import React, { useState, useMemo } from 'react';
import { Building, BarChart3, TrendingUp, BriefcaseIcon, PlusCircle, Percent } from 'lucide-react';
import {
  PieChart as RechartsPieChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Pie,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Sector,
  PieChart,
  ComposedChart,
  Line
} from 'recharts';
import { insurerIssuanceMetrics } from '../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import { numberWithCommas } from '../../../utils/miscFunctions';
import GTable from '../../elements/GTable/GTable';
import DatePickerInput from '../../form-control/DatePickerInput';
import moment from 'moment';
import Button from '../../form-control/Button';

interface IssuanceModeBreakdown {
  MID_LIVE: number;
  OFFLINE: number;
  EMAIL: number;
  INSURER_API: number;
  UNKNOWN: number;
  [key: string]: number;
}

interface IssuanceModePercentages {
  MID_LIVE: number;
  OFFLINE: number;
  EMAIL: number;
  INSURER_API: number;
  UNKNOWN: number;
  [key: string]: number;
}

interface InsurerData {
  issuanceModeBreakdown: IssuanceModeBreakdown;
  issuanceModePercentages: IssuanceModePercentages;
  total: number;
  added: number;
  addedPercentage: number;
  createdLivePercentage: number;
  createdLive: number;
  insurerName: string;
  insurerId: number;
}

interface OverallData {
  issuanceModeBreakdown: IssuanceModeBreakdown;
  issuanceModePercentages: IssuanceModePercentages;
  total: number;
  added: number;
  addedPercentage: number;
  createdLivePercentage: number;
  createdLive: number;
}

interface IssuanceModeMetricsResponse {
  insurers: InsurerData[];
  overall: OverallData;
  from: string;
  to: string;
}

const ActiveShape: React.FC<{
  cx?: number;
  cy?: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}> = ({ cx = 0, cy = 0, innerRadius, outerRadius, startAngle, endAngle, fill }) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
    <Sector
      cx={cx}
      cy={cy}
      startAngle={startAngle}
      endAngle={endAngle}
      innerRadius={outerRadius + 8}
      outerRadius={outerRadius + 10}
      fill={fill}
    />
  </g>
);
const IssuanceModeMetrics: React.FC = () => {
  const [selectedFromDate, setSelectedFromDate] = useState<Date>(
    new Date(new Date().getFullYear(), 0, 1)
  );
  const [selectedToDate, setSelectedToDate] = useState<Date>(new Date());
  const [insurerIssuanceMetricsData, setInsurerIssuanceMetricsData] =
    useState<IssuanceModeMetricsResponse>();
  const getInsurerIssuanceMetrics = async (fromDate: Date, toDate: Date): Promise<any> => {
    const formattedFromDate = moment(fromDate).format('YYYY-MM-DD');
    const formattedToDate = moment(toDate).format('YYYY-MM-DD');
    await insurerIssuanceMetrics(formattedFromDate, formattedToDate).then((res: any) => {
      setInsurerIssuanceMetricsData(res.data);
    }).catch((error:Error) => console.log("error",error));
  };
  const MID_LIVE_COLOR = 'text-[#34d399]';
  const  OFFLINE_COLOR = 'text-[#fb7185]';
  const EMAIL_COLOR = 'text-[#fde68a]';
  const  INSURER_API_COLOR = 'text-[#818cf8]';

  const { isLoading: isLoading } = useQuery({
    queryKey: ['getInsurerIssuanceMetrics'],
    queryFn: () => getInsurerIssuanceMetrics(selectedFromDate, selectedToDate)
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Insurance Company',
            accessor: 'insurerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Total Policies',
            accessor: 'total',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Created Live',
            accessor: 'createdLive',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Live %',
            accessor: 'createdLivePercentage',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },

          {
            Header: 'Added',
            accessor: 'added',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Added %',
            accessor: 'addedPercentage',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Issued Mode',
            accessor: 'issuanceModeBreakdown.MID_LIVE',
            Cell: (row: any) => {
              return (
               <div className="flex items-start gap-2 flex-col">
                <span className="inline-flex px-2 py-1 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800">
                  MID_LIVE: {row.row.original.issuanceModeBreakdown.MID_LIVE || '-'}
                </span>

                <span className="inline-flex px-2 py-1 text-[10px] font-semibold rounded bg-rose-100 text-rose-800">
                  OFFLINE: {row.row.original.issuanceModeBreakdown.OFFLINE || '-'}
                </span>

                <span className="inline-flex px-2 py-1 text-[10px] font-semibold rounded bg-indigo-100 text-indigo-800">
                  INSURER_API: {row.row.original.issuanceModeBreakdown.INSURER_API || '-'}
                </span>

                <span className="inline-flex px-2 py-1 text-[10px] font-semibold rounded bg-amber-100 text-amber-800">
                  EMAIL: {row.row.original.issuanceModeBreakdown.EMAIL || '-'}
                </span>
              </div>

              );
            }
          }
        ]
      }
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 dark:bg-darkBg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!insurerIssuanceMetricsData) return null;

  const issuancePieData = insurerIssuanceMetricsData?.overall
    ? [
        {
          mode: 'MID LIVE',
          count: insurerIssuanceMetricsData.overall.issuanceModeBreakdown?.MID_LIVE ?? 0,
          percentage: insurerIssuanceMetricsData.overall.issuanceModePercentages?.MID_LIVE ?? 0,
          color: '#34d399'
        },
        {
          mode: 'EMAIL',
          count: insurerIssuanceMetricsData.overall.issuanceModeBreakdown?.EMAIL ?? 0,
          percentage: insurerIssuanceMetricsData.overall.issuanceModePercentages?.EMAIL ?? 0,
          color: '#fbbf24'
        },
        {
          mode: 'INSURER API',
          count: insurerIssuanceMetricsData.overall.issuanceModeBreakdown?.INSURER_API ?? 0,
          percentage: insurerIssuanceMetricsData.overall.issuanceModePercentages?.INSURER_API ?? 0,
          color: '#818cf8'
        },
        {
          mode: 'OFFLINE',
          count: insurerIssuanceMetricsData.overall.issuanceModeBreakdown?.OFFLINE ?? 0,
          percentage: insurerIssuanceMetricsData.overall.issuanceModePercentages?.OFFLINE ?? 0,
          color: '#fb7185'
        }
      ]
    : [];

const issuanceBarData = insurerIssuanceMetricsData?.insurers
  ? insurerIssuanceMetricsData.insurers.map((values) => ({
      name: values.insurerName,
      MID_LIVE: values.issuanceModeBreakdown.MID_LIVE,
      OFFLINE: values.issuanceModeBreakdown.OFFLINE,
      EMAIL: values.issuanceModeBreakdown.EMAIL,
      INSURER_API: values.issuanceModeBreakdown.INSURER_API,
    }))
  : [];
 
  const CustomPieChartTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700 shadow-md rounded-md">
          <p className="font-medium text-gray-900 dark:text-white">Mode: {data.mode}</p>
          <p className="text-sm text-gray-600 dark:text-white">Count: {data.count}</p>
          <p className="text-sm text-gray-600 dark:text-white">
            Percentage: {data.percentage?.toFixed(2) || '0.0'}%
          </p>
        </div>
      );
    }
    return null;
  };

const CustomBarChartTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    const total =
      (data.MID_LIVE ?? 0) +
      (data.OFFLINE ?? 0) +
      (data.EMAIL ?? 0);
      (data.INSURER_API ?? 0);

    return (
      <div className="bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700 shadow-md rounded-md">
        <p className="font-medium text-gray-900 dark:text-white mb-2">
          {data.name}
        </p>
        <div className="space-y-1 text-sm">
          <p className={`flex justify-between text-[11.5px] ${INSURER_API_COLOR}`}>
            <span>INSURER API</span>
            <span className="font-medium">
              {numberWithCommas(data.INSURER_API ?? 0)}
            </span>
          </p>

          <p className={`flex justify-between text-[11.5px] ${EMAIL_COLOR}`}>
            <span>EMAIL</span>
            <span className="font-medium">
              {numberWithCommas(data.EMAIL ?? 0)}
            </span>
          </p>

          <p className={`flex justify-between text-[11.5px]  ${OFFLINE_COLOR}`}>
            <span>OFFLINE</span>
            <span className="font-medium">
              {numberWithCommas(data.OFFLINE ?? 0)}
            </span>
          </p>

          <p className={`flex justify-between text-[11.5px] ${MID_LIVE_COLOR}`}>
            <span>MID LIVE</span>
            <span className="font-medium">
              {numberWithCommas(data.MID_LIVE ?? 0)}
            </span>
          </p>
          <hr className="my-2 border-gray-200 dark:border-gray-700" />

          <p className="flex justify-between text-[12px] font-semibold text-gray-900 dark:text-white">
            <span>TOTAL</span>
            <span>{numberWithCommas(total)}</span>
          </p>
        </div>

      </div>
    );
  }

  return null;
};

  const issuancePieChart = () => {
    return (
      <>
        <div className="h-[320px] sm:h-[450px] bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <h3 className="text-sm font-medium text-gray-700 dark:text-darkBg-mainText">
            Issued Mode Distribution
          </h3>
          <ResponsiveContainer width="100%" height="80%">
            <PieChart>
              <Pie
                activeShape={ActiveShape as any}
                data={issuancePieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="count">
                {issuancePieData.map((entry: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    fillOpacity={0.8}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomPieChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {/* Custom legend */}
          <div className="flex w-full flex-wrap text-xs items-center justify-center gap-3">
            {issuancePieData.map((item: any, index: number) => (
              
                item.count !== 0 && (<div  key={index} className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-gray-600 dark:text-gray-300">
                    {item.mode}
                  </span>
                </div>)
            
            ))}
        </div>
      </div>
      </>
    );
  };

  const issuanceBarChart = () => {
    return (
      <div className="h-[360px] sm:h-[450px] bg-white dark:bg-darkBg rounded-2xl  shadow-sm border border-gray-200 dark:border-darkBg-border p-4 sm:p-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-darkBg-mainText">
          Policies by Insurer
        </h3>
        <div className="h-[calc(100%-20px)] overflow-x-auto">
          <div className="h-full min-w-[560px] sm:min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={issuanceBarData}
                margin={{ top: 30, right: 30, left: 5, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis
                  domain={[0, 10]}
                  angle={-45}
                  dataKey="name"
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                  padding={{ left: 10, right: 10 }}
                  height={60}
                  interval={0}
                />
                <YAxis
                  domain={[0, 'auto']}
                  allowDecimals={false}
                  tickFormatter={(value) => numberWithCommas(value)}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip content={<CustomBarChartTooltip />} />
                {/* <Bar dataKey={'chart'} fill={'#818cf8'} radius={[4, 4, 0, 0]} barSize={30} /> */}
      
              <Bar dataKey="MID_LIVE" stackId="a" fill="#6ee7b7" barSize={35} />
              <Bar dataKey="OFFLINE" stackId="a" fill="#fb7185" barSize={35} />
              <Bar dataKey="EMAIL" stackId="a" fill="#fde68a" barSize={35} />
              <Bar dataKey="INSURER_API" stackId="a" fill="#c7d2fe" barSize={35} />

                <Line
                  type="monotone"
                  dataKey="target"
                  name="Target"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: 0 }}
                  activeDot={{ r: 6 }}
                  label="Target"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const NotFoundInsurersData = () => {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border dark:bg-transparent border-gray-200 dark:border-gray-700 p-6 h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-transparent rounded-lg p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
            <BriefcaseIcon className="w-8 h-8 text-gray-400 dark:text-gray-400" />
          </div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">No insurer data</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
            There is no insurance company records available for the selected periods. Try selecting
            a different date or check back later for updates.
          </p>
        </div>
      </div>
    );
  };
 
  const metricsCards = [
       {
    name:'Total Issued Policies',
    value: formatCurrency(insurerIssuanceMetricsData.overall.total) ?? 0,
    icon: <BarChart3 className="h-8 w-8 text-blue-500" />,
    nameColor: 'text-blue-600',
    valueColor: 'text-blue-900',
    bgColor: 'bg-blue-50',
  },
     {
    name:'Created Live',
    value: formatCurrency(insurerIssuanceMetricsData.overall.createdLive) ?? 0,
    icon: <TrendingUp className="h-8 w-8 text-green-500" />,
    nameColor: 'text-green-600',
    valueColor: 'text-green-900',
    bgColor: 'bg-green-50',
  },
 {
    name:'Live Percentage',
    value: `${insurerIssuanceMetricsData.overall.createdLivePercentage.toFixed(2)}%`,
    icon: <Percent className="h-8 w-8 text-rose-500" />,
    nameColor: 'text-purple-600',
    valueColor: 'text-purple-900',
    bgColor: 'bg-purple-50',
  },
 {
  name: 'Added',
  value: formatCurrency(insurerIssuanceMetricsData.overall.added) ?? 0,
  icon: <PlusCircle className="h-8 w-8 text-teal-500" />,
  nameColor: 'text-teal-600',
  valueColor: 'text-teal-900',
  bgColor: 'bg-teal-50',
},
{
  name: 'Added Percentage',
  value: `${insurerIssuanceMetricsData.overall.addedPercentage.toFixed(2)}%`,
  icon: <Percent className="h-8 w-8 text-rose-500" />,
  nameColor: 'text-rose-600',
  valueColor: 'text-rose-900',
  bgColor: 'bg-rose-50',
},
 {
    name:'Active Insurers',
    value: insurerIssuanceMetricsData.insurers.length ?? 0,
    icon: <Building className="h-8 w-8 text-orange-500" />,
    nameColor: 'text-orange-600',
    valueColor: 'text-orange-900',
    bgColor: 'bg-orange-50',
  },]

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6 dark:bg-darkBg dark:border dark:border-darkBg-border dark:rounded-md">
      {/* Header */}
      <div className="flex flex-wrap flex-col md:flex-row gap-2 justify-between  items-start md:items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <Building className="h-5 w-5 mr-2 text-blue-600" />
            Insurers Issued Policies Metrics
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Period: {moment(selectedFromDate).format('DD-MM-YYYY')} to{' '}
            {moment(selectedToDate).format('DD-MM-YYYY')}
          </p>
        </div>

        <div className="flex flex-wrap flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className='flex md:items-center justify-between gap-4'>

            <div className="w-40 sm:w-44 flex items-center gap-2">
              <h4 className="hidden sm:block text-gray-500 text-base">From:</h4>
              <DatePickerInput
                id="from"
                selected={selectedFromDate}
                onChange={(date: Date | null) => date && setSelectedFromDate(date)}
                allowFutureSelections={false}
                maxDate={new Date()}
                classes="w-full"
              />
            </div>
            <div className="w-40 sm:w-40 flex items-center gap-2">
              <h4 className="hidden sm:block text-gray-500 text-base">To:</h4>
              <DatePickerInput
                id="to"
                selected={selectedToDate}
                allowFutureSelections={false}
                onChange={(date: Date | null) => date && setSelectedToDate(date)}
                maxDate={new Date()}
                classes="w-full"
              />
            </div>

          </div>
          <div className='w-full sm:w-fit flex'>
            <Button
              type="submit"
              label={'Submit'}
              bgColor="primary"
              fullWidth
              onClick={() => getInsurerIssuanceMetrics(selectedFromDate, selectedToDate)}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {metricsCards?.map((metric, i:number)=> (
          <div key={i} className={`${metric.bgColor} p-4 rounded-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${metric.nameColor}`}>{metric.name}</p>
              <p className={`text-2xl font-bold ${metric.valueColor}`}>
                {metric.value}
              </p>
            </div>
            {metric.icon}
          </div>
        </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Pie Chart */}
        <div className="h-full col-span-1">
          {insurerIssuanceMetricsData.insurers.length !== 0 ? (
            issuancePieChart()
          ) : (
            <NotFoundInsurersData />
          )}
        </div>
        {/* Bar Chart */}
        <div className="col-span-2">
          {insurerIssuanceMetricsData.insurers.length !== 0 ? (
            issuanceBarChart()
          ) : (
            <NotFoundInsurersData />
          )}
        </div>
      </div>

      {/* Insurers Table */}
      <div className="bg-gray-50 dark:bg-darkBg rounded-lg overflow-x-auto dark:border dark:border-darkBg-border dark:rounded-md">
        <div className="px-4 py-3 bg-gray-100 dark:bg-darkBg  border-b border-gray-200 dark:border-darkBg-border">
          <h3 className="text-lg font-medium text-gray-700 dark:text-darkBg-mainText">
            Insurance Company Breakdown
          </h3>
        </div>
        <div className="">
          <GTable
            columns={columns}
            data={insurerIssuanceMetricsData.insurers || []}
            showSearch={false}
            tableType={'data'}
            loading={isLoading}
            stickyHeader
            classes='!max-h-[500px]'
          />
        </div>
      </div>
    </div>
  );
};

export default IssuanceModeMetrics;
